<template>
    <v-app id="app">
        <!-- App bar -->
        <v-app-bar app clipped-right color="bal-gray" light flat>
            <v-app-bar-nav-icon large @click.stop="toggleDrawer" />
            <v-toolbar-title
                >{{ `${$route.meta.title}` | t
                }}<span style="color:gray;">{{ ` (${shopsCount})` }}</span></v-toolbar-title
            >
            <v-combobox
                v-if="hasPermissionForAction(PermissionsConstants.SHOP, PermissionsConstants.SEARCH)"
                :items="searchHistory"
                :value="searchShopString"
                @input="controlSearchString($event)"
                item-text="keyword"
                id="searchInput"
                class="mt-6 mx-2"
                :placeholder="'Shop name, company, type, city, product name, product code' | t"
                prepend-inner-icon="search"
                flat
                solo
                dense
                clearable
            >
                <template slot="item" slot-scope="data">
                    <span @click="updateShopSearch(data.item.keyword)" style="width: 100%; height: 100%" class="pa-2">{{
                        data.item.keyword
                    }}</span>
                </template>
            </v-combobox>
            <v-spacer v-else />

            <v-menu bottom left offset-y v-if="user.role === 'superadmin'">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="px-2 ml-2 mx-2" color="bal-dark-gray" dark style="min-width: 0;">
                        <v-icon>{{ "fas fa-file-export" }}</v-icon>
                    </v-btn>
                </template>
                <v-list style="width: 160px;">
                    <v-list-item @click="toggleExportExcel">
                        <v-list-item-title style="color: red; text-align: center;"
                            >{{ "Export" | t }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                        <v-file-input
                            show-size
                            hide-details
                            dense
                            accept=".xlsx, .xls"
                            :label="'Upload' | t"
                            v-model="fileUploadValue"
                            @change="uploadExcel($event)"
                        />
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-btn
                dark
                color="bal-dark-gray"
                class="px-2"
                @click.stop="toggleListSettingsModal"
                v-if="hasPermissionForAction(PermissionsConstants.SHOP, PermissionsConstants.FILTER)"
                style="min-width: 0;"
            >
                <v-icon>settings</v-icon>
            </v-btn>
            <v-btn
                dark
                color="bal-red"
                class="px-2 ml-2"
                @click.stop="openNewShopDrawer"
                v-if="hasPermissionForAction(PermissionsConstants.SHOP, PermissionsConstants.CREATE)"
                style="min-width: 0;"
            >
                <v-icon>add</v-icon>
            </v-btn>
        </v-app-bar>

        <!-- Drawer left -->
        <main-drawer :drawer-open="mainDrawerOpen" @close="toggleDrawer" />

        <v-navigation-drawer :value="drawerOpen" fixed right hide-overlay width="900">
            <shops-admin-view v-if="$route.name === 'shops-admin-view'"></shops-admin-view>
            <shops-admin-edit v-if="$route.name === 'shops-admin-edit'"></shops-admin-edit>
        </v-navigation-drawer>
        <!-- Main content -->
        <v-main>
            <shops-admin-list />
        </v-main>

        <shops-admin-list-settings-modal />

        <v-snackbar color="success" bottom :timeout="2500" :value="requestSuccess">
            <div style="display: flex; flex-direction: row;">
                <v-icon color="#FFF">check</v-icon>
                <div class="title pa-2 ">{{ "Success" | t }}</div>
            </div>
        </v-snackbar>
        <!-- Error snackbar -->
        <v-snackbar color="error" bottom :timeout="2500" :value="requestError">
            <div style="display: flex; flex-direction: row;">
                <v-icon color="#FFF">error</v-icon>
                <div class="title pa-2 ">{{ errorMessage }}</div>
            </div>
        </v-snackbar>
        <!-- Footer -->
        <page-footer />
    </v-app>
</template>

<script>
import ShopsAdminList from "@/pages/shops-admin/shops-admin-list";
import ShopsAdminView from "../pages/shops-admin/shops-admin-view";
import ShopsAdminEdit from "../pages/shops-admin/shops-admin-edit";
import PageFooter from "@/components/page-footer";
import MainDrawer from "@/components/main-drawer";
import ShopsAdminListSettingsModal from "@/pages/shops-admin/shops-admin-list-settings-modal";
import { mapGetters, mapMutations, mapState, mapActions } from "vuex";
import Store from "@/store";
import settings from "@/settings";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "shops-admin-layout",
    components: {
        ShopsAdminList,
        PageFooter,
        MainDrawer,
        ShopsAdminView,
        ShopsAdminEdit,
        ShopsAdminListSettingsModal
    },
    data() {
        return {
            mainDrawerOpen: null,
            fileUploadValue: null
        };
    },
    async created() {
        await Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
        const drawerState = localStorage.getItem("mainDrawerOpen");
        if (drawerState) {
            this.mainDrawerOpen = JSON.parse(drawerState);
        } else {
            this.mainDrawerOpen = true;
        }
    },
    destroyed() {
        if (this.searchShopString.length > 0) Store.commit("ShopsAdminStore/resetShopSearch");
        document.removeEventListener("keydown", this.handleKeyboardListener);
        Store.commit("ShopsAdminStore/setRequestSuccess", false);
        Store.commit("ShopsAdminStore/setErrorMessage", "");
    },
    watch: {
        requestSuccess: value => {
            if (value) setTimeout(() => Store.commit("ShopsAdminStore/setRequestSuccess", false), 2500);
        },
        requestError: value => {
            if (value)
                setTimeout(() => {
                    Store.commit("ShopsAdminStore/setRequestError", false);
                    Store.commit("ShopsAdminStore/setErrorMessage", "");
                }, 2500);
        }
    },
    methods: {
        ...mapMutations("ShopsAdminStore", ["resetActiveShop", "toggleListSettingsModal"]),
        ...mapActions("ShopsAdminStore", ["importExcel"]),
        ...mapActions("SearchHistoryStore", ["getSearchHistory"]),
        openNewShopDrawer() {
            this.$router.push({ name: "shops-admin-edit", params: { shopId: "new" } });
        },
        updateShopSearch(value) {
            Store.commit("ShopsAdminStore/updateShopSearch", { term: value });
        },
        toggleDrawer() {
            this.mainDrawerOpen = !this.mainDrawerOpen;
            localStorage.setItem("mainDrawerOpen", this.mainDrawerOpen);
        },
        handleKeyboardListener(e) {
            if (e.keyCode === 13 && document.activeElement.tagName === "INPUT") {
                Store.commit("ShopsAdminStore/updateShopSearch", {
                    term: document.querySelector("#searchInput").value
                });
            }
            if (e.keyCode === 27 && this.drawerOpen) {
                this.resetActiveShop();
                this.$router.push({ name: "shops-admin-list" });
            }
            if (
                this.activeShop &&
                Object.keys(this.activeShop).length > 0 &&
                this.activeShop.id !== "new" &&
                this.$route.name !== "shops-admin-edit"
            ) {
                let currentProductIndex = this.shops.findIndex(p => p.id === this.activeShop.id);
                if (e.key === "ArrowUp") {
                    if (currentProductIndex !== 0) {
                        const newProductId = this.shops[currentProductIndex - 1].id;
                        Store.dispatch("ShopsAdminStore/getShopData", newProductId);
                        this.$router.push({ name: this.$route.name, params: { shopId: newProductId } });
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentProductIndex + 1 !== this.shops.length) {
                        const newProductId = this.shops[currentProductIndex + 1].id;
                        Store.dispatch("ShopsAdminStore/getShopData", newProductId);
                        this.$router.push({ name: this.$route.name, params: { shopId: newProductId } });
                    }
                }
            }
        },
        controlSearchString(value) {
            if (value == "") {
                Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
                this.updateShopSearch(value);
            }
        },
        toggleExportExcel() {
            if (confirm("Are you sure?")) {
                window.location.href = settings.api_url + "/shops-excel/";
            }
        },
        async uploadExcel(e) {
            if (e) {
                await this.importExcel(e);
                this.fileUploadValue = null;
            }
        }
    },
    computed: {
        ...mapState("ShopsAdminStore", [
            "drawerOpen",
            "searchShopString",
            "shops",
            "activeShop",
            "requestSuccess",
            "requestError",
            "errorMessage"
        ]),
        ...mapState(["user"]),
        ...mapGetters("ShopsAdminStore", ["shopsCount"]),
        ...mapState("SearchHistoryStore", ["searchHistory"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        }
    }
};
</script>

<style scoped></style>
